import { ChangeEventHandler, useEffect, useMemo, useRef, useState } from 'react';
import { ClassNames, DayPicker, SelectSingleEventHandler } from 'react-day-picker';
import dayPickerStyles from 'react-day-picker/dist/style.module.css';
import { useOutsideClick } from '../../CustomHooks';
import datePickerStyles from '../DateRangePicker/DateRangePicker.module.scss';
import {
  formatAttendanceDateForControl,
  getCanonicalAttendanceDateFromDate,
  parseAttendanceDateInput,
  parseCanonicalAttendanceDateAsLocalDate,
} from '../../TakweenAttendance/attendanceHelpers';

interface SingleDatePickerProps {
  inputId?: string;
  value: string;
  onChange: (attendanceDate: string) => void;
  disabled?: boolean;
  locale?: string;
}

const SingleDatePicker = ({ inputId, value, onChange, disabled = false, locale = 'en-US' }: SingleDatePickerProps) => {
  const [displayValue, setDisplayValue] = useState<string>(formatAttendanceDateForControl(value, locale));
  const [isDisplayCalendar, setIsDisplayCalendar] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setDisplayValue(formatAttendanceDateForControl(value, locale));
  }, [value, locale]);

  useOutsideClick(containerRef, () => setIsDisplayCalendar(false));

  const selectedDate = useMemo(() => parseCanonicalAttendanceDateAsLocalDate(value), [value]);

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const nextDisplayValue = event.target.value;
    setDisplayValue(nextDisplayValue);

    const parsedAttendanceDate = parseAttendanceDateInput(nextDisplayValue, locale);
    if (parsedAttendanceDate) {
      onChange(parsedAttendanceDate);
    }
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsDisplayCalendar(true);
    }
  };

  const handleFieldClick = () => {
    if (disabled) {
      return;
    }

    setIsDisplayCalendar(true);
    inputRef.current?.focus();
  };

  const handleInputBlur = () => {
    setDisplayValue(formatAttendanceDateForControl(value, locale));
  };

  const handleDateSelect: SelectSingleEventHandler = (date) => {
    if (!date) {
      return;
    }

    const canonicalAttendanceDate = getCanonicalAttendanceDateFromDate(date);
    onChange(canonicalAttendanceDate);
    setIsDisplayCalendar(false);
  };

  const dayPickerClassNames: ClassNames = {
    ...dayPickerStyles,
    head: datePickerStyles.days,
    root: datePickerStyles.dayPickerRoot,
    caption_label: datePickerStyles.captionLabel,
    nav_button: datePickerStyles.dayPickerNav,
    day_selected: datePickerStyles.daySelected,
    cell: datePickerStyles.cell,
    day: datePickerStyles.day,
    button: datePickerStyles.dayPickerButton,
  };

  return (
    <div className={datePickerStyles.singleDateContainer} ref={containerRef}>
      <form className={datePickerStyles.singleDateForm} onSubmit={(event) => event.preventDefault()}>
        <div
          className={[
            datePickerStyles.singleDateField,
            disabled ? datePickerStyles.singleDateFieldLocked : datePickerStyles.singleDateFieldInteractive,
          ].join(' ')}
          onClick={handleFieldClick}
        >
          <input
            id={inputId}
            ref={inputRef}
            disabled={disabled}
            aria-label='Attendance date'
            className={[
              datePickerStyles.singleDateInput,
              disabled ? '' : datePickerStyles.singleDateInputUnlocked,
            ].join(' ')}
            placeholder={formatAttendanceDateForControl('2026-06-02', locale)}
            value={displayValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
          />
          {!disabled ? (
            <svg
              viewBox='0 0 20 20'
              aria-hidden='true'
              className={datePickerStyles.singleDateChevron}
              width='20'
              height='20'
            >
              <path
                d='M5.5 7.5 10 12l4.5-4.5'
                fill='none'
                stroke='currentColor'
                strokeWidth='1.8'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          ) : null}
        </div>
      </form>
      {isDisplayCalendar ? (
        <div className={datePickerStyles.calendarContainer}>
          <DayPicker
            disabled={disabled}
            mode='single'
            classNames={dayPickerClassNames}
            month={selectedDate || undefined}
            selected={selectedDate || undefined}
            onSelect={handleDateSelect}
            showOutsideDays
          />
        </div>
      ) : null}
    </div>
  );
};

export default SingleDatePicker;
